import { normalize } from "normalizr";
import { parse, stringify } from "query-string";
import { change, stopSubmit, touch } from "redux-form";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import { login as loginActionCreator } from "@/mk2/actions";
import { bazaarUrl } from "@/mk2/apps/bazaar/urls";
import { homepageUrl } from "@/mk2/apps/home/urls";
import { LoginNormalizedResponse } from "@/mk2/apps/users/containers/Login/Login.actions";
import {
    SIGNUP_LOAD_TRIGGER,
    SIGNUP_PHONE_REQUEST_TOKEN_TRIGGER,
    SIGNUP_PHONE_VALIDATE_TOKEN_TRIGGER,
    SIGNUP_TRIGGER,
    SIGNUP_VALIDATE_REGISTRATION_INPUTS_TRIGGER,
    SignupLoadNormalizedResponse,
    SignupLoadTriggerAction,
    SignupNormalizedResponse,
    SignupPhoneRequestTokenNormalizedResponse,
    SignupPhoneRequestTokenTriggerAction,
    SignupPhoneValidateTokenTriggerAction,
    SignupTriggerAction,
    SignupValidateRegistrationInputsTriggerAction,
    signupAPI,
    signupLoadApi,
    signupPhoneRequestTokenApi,
    signupPhoneValidateTokenApi,
} from "@/mk2/apps/users/containers/Signup/Signup.actions";
import { SIGNUP_FORM_NAME } from "@/mk2/apps/users/containers/Signup/Signup.constants";
import { signupUrl } from "@/mk2/apps/users/urls";
import {
    gotoUrlAfterLoginOrSignup,
    gtmEvent,
    hasValidationViaSms,
    isNextPagePWA,
    storeLastKnownUsernameCookie,
} from "@/mk2/apps/users/utils";
import {
    pageHeaderFetchFlaggedCountsTrigger,
    pageHeaderLoad,
} from "@/mk2/containers/PageHeader/PageHeader.action";
import {
    XHRAction,
    XHRFormError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { showSuccessToast } from "@/mk2/helpers/toasts";
import { removeEmpty } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import {
    AppStateProvidedFromServer,
    getRequestBaseUrl,
    getRequestPermissions,
} from "@/mk2/reducers";
import { UserEntity, UserSchema } from "@/mk2/schemas";
import { HistoryAction, pushInPWA } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import {
    SIGNUP_PHONE_WE_SENT_YOU_NEW_ACTIVATION_CODE_CALL,
    SIGNUP_PHONE_WE_SENT_YOU_NEW_ACTIVATION_CODE_SMS,
    YOU_HAVE_BEEN_SUCCESSFULLY_REGISTERED,
    YOU_WERE_SUCCESSFULLY_LOGGED_IN_AS_username,
} from "@/mk/autogenerated/translation";
import { canManageBlogs, canManageForum, canManageGroups } from "@/mk/bazaar/common/userUtils";
import {
    phone_api_verification_send_token_url,
    phone_api_verification_validate_token_url,
    users_api_auth_signup_load_url,
    users_api_auth_signup_url,
    users_api_auth_validate_inputs_url,
} from "@/mk/urls/functions";

const logger = getLogger("home/Signup.sagas");

interface ApiResponseSignupLoad {
    body: {
        entities: SignupLoadNormalizedResponse["entities"];
        result: SignupLoadNormalizedResponse["result"];
    };
}

function* load({
    contentItemsKey,
    xhr,
    location,
    routeNavigate,
}: SignupLoadTriggerAction & XHRAction & HistoryAction) {
    yield put(signupLoadApi.request(contentItemsKey));

    try {
        const response: ApiResponseSignupLoad = yield call(() =>
            xhr.get(users_api_auth_signup_load_url(), {
                contentItemsKey,

                // previous url (from standard window.document.referrer)
                pageRef: typeof window !== "undefined" ? window.document.referrer : "",

                // previous url (within pwa from react router)
                pwaRef: location.state?.UNSAFE_mkGoBackUrl || "",
            }),
        );
        yield put(
            signupLoadApi.success(contentItemsKey, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(signupLoadApi.failure(contentItemsKey, normalizeError(error)));
    }
}

interface ApiResponseSignup {
    body: {
        user: UserEntity;
        initialState: AppStateProvidedFromServer;
        loginTriedOnSignup: boolean;
    };
}

function* signup({
    signupFd,
    query,
    xhr,
    location,
    routeNavigate,
}: SignupTriggerAction & XHRAction & HistoryAction) {
    yield put(signupAPI.request(signupFd, query));

    try {
        gtmEvent("event_signup_submitted");

        const response: ApiResponseSignup = yield call(() =>
            xhr.post(users_api_auth_signup_url(), {
                signupFd,
                query,
            }),
        );

        // Login in redux before any URL redirect
        yield put(loginActionCreator(response.body.initialState));

        const normalizedResponse: SignupNormalizedResponse = normalize(response.body, {
            user: UserSchema,
        });
        storeLastKnownUsernameCookie(response.body.user.username);

        const msg = response.body.loginTriedOnSignup
            ? interpolate(YOU_WERE_SUCCESSFULLY_LOGGED_IN_AS_username, {
                  username: response.body.user.username,
              })
            : interpolate(YOU_HAVE_BEEN_SUCCESSFULLY_REGISTERED, {
                  username: response.body.user.username,
              });
        const baseUrl = yield select(getRequestBaseUrl);
        const canRedirectInPWA = isNextPagePWA(location, homepageUrl);
        const gotoUrl = gotoUrlAfterLoginOrSignup(baseUrl, location, bazaarUrl, canRedirectInPWA);

        xhr.refreshApiAuth();
        if (canRedirectInPWA) {
            yield put(pageHeaderLoad());
            const requestUserPermissions: string[] = yield select(getRequestPermissions);
            const isBlogsAdmin = canManageBlogs(requestUserPermissions);
            const isForumAdmin = canManageForum(requestUserPermissions);
            const isGroupAdmin = canManageGroups(requestUserPermissions);
            if (isBlogsAdmin || isForumAdmin || isGroupAdmin) {
                yield put(pageHeaderFetchFlaggedCountsTrigger());
            }
        }

        yield handleXHRFormSuccessSaga(SIGNUP_FORM_NAME, routeNavigate, gotoUrl, msg);
        yield put(
            signupAPI.success(
                signupFd,
                response.body.user,
                response.body.initialState,
                normalizedResponse,
            ),
        );
        if (response.body.loginTriedOnSignup) {
            gtmEvent("event_login_success");
        } else {
            gtmEvent("event_signup_success");
        }
    } catch (error) {
        yield handleXHRFormErrorSaga(SIGNUP_FORM_NAME, error as XHRFormError, logger);
        yield put(signupAPI.failure(signupFd, normalizeError(error)));
    }
}

interface ApiResponseValidateInputsAndLogin {
    body: {
        user: UserEntity;
        initialState: AppStateProvidedFromServer;
    };
}

interface ApiResponseValidateInputs {
    body: {
        status: string;
    };
}

function* validateInputs({
    signupFd,
    query,
    xhr,
    location,
    routeNavigate,
}: SignupValidateRegistrationInputsTriggerAction & XHRAction & HistoryAction) {
    yield put(touch(SIGNUP_FORM_NAME, "dueDate", "username", "password", "email"));
    try {
        gtmEvent("event_signup_validate_inputs");
        const response: ApiResponseValidateInputsAndLogin | ApiResponseValidateInputs = yield call(
            () =>
                xhr.post(users_api_auth_validate_inputs_url(), {
                    signupFd,
                    query,

                    // previous url (from standard window.document.referrer)
                    pageRef: typeof window !== "undefined" ? window.document.referrer : "",

                    // previous url (within pwa from react router)
                    pwaRef: location.state?.UNSAFE_mkGoBackUrl || "",
                }),
        );

        if ("user" in response.body) {
            // Login in redux before any URL redirect
            yield put(loginActionCreator(response.body.initialState));

            // LOGIN - user logged in (using signup inputs)
            const normalizedResponse: LoginNormalizedResponse = normalize(response.body, {
                user: UserSchema,
            });
            storeLastKnownUsernameCookie(response.body.user.username);

            const msg = interpolate(YOU_WERE_SUCCESSFULLY_LOGGED_IN_AS_username, {
                username: response.body.user.username,
            });
            const baseUrl = yield select(getRequestBaseUrl);
            const canRedirectInPWA = isNextPagePWA(location, homepageUrl);
            const gotoUrl = gotoUrlAfterLoginOrSignup(
                baseUrl,
                location,
                homepageUrl,
                canRedirectInPWA,
            );

            xhr.refreshApiAuth();
            if (canRedirectInPWA) {
                yield put(pageHeaderLoad());
                const requestUserPermissions: string[] = yield select(getRequestPermissions);
                const isBlogsAdmin = canManageBlogs(requestUserPermissions);
                const isForumAdmin = canManageForum(requestUserPermissions);
                const isGroupAdmin = canManageGroups(requestUserPermissions);
                if (isBlogsAdmin || isForumAdmin || isGroupAdmin) {
                    yield put(pageHeaderFetchFlaggedCountsTrigger());
                }
            }

            yield handleXHRFormSuccessSaga(SIGNUP_FORM_NAME, routeNavigate, gotoUrl, msg);
            gtmEvent("event_login_success");
        } else {
            // SIGNUP - inputs validated
            const qs = parse(location.search);
            pushInPWA(routeNavigate, {
                pathname: signupUrl,
                search: stringify(removeEmpty({ ...qs, step: 2 })),
            });
        }
    } catch (error) {
        yield handleXHRFormErrorSaga(SIGNUP_FORM_NAME, error as XHRFormError, logger);
    }
}

interface ApiResponseRequestToken {
    body: SignupPhoneRequestTokenNormalizedResponse;
}

function* requestToken({
    countryPrefix,
    rawPhoneNumber,
    resend,
    xhr,
    location,
    routeNavigate,
}: SignupPhoneRequestTokenTriggerAction & XHRAction & HistoryAction) {
    yield put(signupPhoneRequestTokenApi.request(countryPrefix, rawPhoneNumber));
    yield put(touch(SIGNUP_FORM_NAME, "rawPhoneNumber", "countryPrefix"));

    try {
        gtmEvent("event_signup_request_token");
        const response: ApiResponseRequestToken = yield call(() =>
            xhr.post(phone_api_verification_send_token_url(), {
                mode: "signup",
                requestTokenFd: {
                    countryPrefix,
                    rawPhoneNumber,
                },
            }),
        );

        // Stop submit
        yield put(
            change(SIGNUP_FORM_NAME, "fullPhoneNumber", response.body.result.fullPhoneNumber),
        );
        yield put(stopSubmit(SIGNUP_FORM_NAME));

        yield put(signupPhoneRequestTokenApi.success(countryPrefix, rawPhoneNumber, response.body));

        // Go to next step
        const qs = parse(location.search);
        pushInPWA(routeNavigate, {
            pathname: signupUrl,
            search: stringify(removeEmpty({ ...qs, step: 3 })),
        });
        if (resend) {
            yield showSuccessToast(
                hasValidationViaSms(response.body.result.fullPhoneNumber)
                    ? SIGNUP_PHONE_WE_SENT_YOU_NEW_ACTIVATION_CODE_SMS
                    : SIGNUP_PHONE_WE_SENT_YOU_NEW_ACTIVATION_CODE_CALL,
            );
        }
    } catch (error) {
        // the phone field validation error comes back as 422 (JSONErrorForm), not 400
        if (resend && error.status === 422) {
            // chceme zobrazit chybu na formfielde rawPhoneNumber
            const qs = parse(location.search);
            pushInPWA(routeNavigate, {
                pathname: signupUrl,
                search: stringify(removeEmpty({ ...qs, step: 2 })),
            });
        }
        yield handleXHRFormErrorSaga(SIGNUP_FORM_NAME, error as XHRFormError, logger);
        yield put(
            signupPhoneRequestTokenApi.failure(
                countryPrefix,
                rawPhoneNumber,
                normalizeError(error),
            ),
        );
    }
}

function* validateToken({
    token,
    fullPhoneNumber,
    xhr,
    location,
    routeNavigate,
}: SignupPhoneValidateTokenTriggerAction & XHRAction & HistoryAction) {
    yield put(signupPhoneValidateTokenApi.request(token, fullPhoneNumber));
    yield put(touch(SIGNUP_FORM_NAME, "token"));

    try {
        gtmEvent("event_signup_validate_token");
        yield call(() =>
            xhr.post(phone_api_verification_validate_token_url(), {
                mode: "signup",
                validateTokenFd: {
                    token,
                    fullPhoneNumber,
                },
            }),
        );

        // Stop submit
        yield put(stopSubmit(SIGNUP_FORM_NAME));
        yield put(signupPhoneValidateTokenApi.success(token, fullPhoneNumber, null));

        // Go to next step
        const qs = parse(location.search);
        pushInPWA(routeNavigate, {
            pathname: signupUrl,
            search: stringify(removeEmpty({ ...qs, step: 4 })),
        });
    } catch (error) {
        yield handleXHRFormErrorSaga(SIGNUP_FORM_NAME, error as XHRFormError, logger);
        yield put(
            signupPhoneValidateTokenApi.failure(token, fullPhoneNumber, normalizeError(error)),
        );
    }
}

export default function* root() {
    yield all([
        takeLatest(SIGNUP_LOAD_TRIGGER, load),
        takeLatest(SIGNUP_TRIGGER, signup),
        takeLatest(SIGNUP_VALIDATE_REGISTRATION_INPUTS_TRIGGER, validateInputs),
        takeLatest(SIGNUP_PHONE_REQUEST_TOKEN_TRIGGER, requestToken),
        takeLatest(SIGNUP_PHONE_VALIDATE_TOKEN_TRIGGER, validateToken),
    ]);
}
